import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useRequest } from "../hooks/useRequest"
import { appStore } from "../store/appStore"
import { isCrawlablePublicPath, isSearchBot } from "../lib/isSearchBot"
import { resolveLocationOrOpenPicker } from "../lib/locationResolve"

type RequireLocationProps = {
    children: React.ReactNode
}

export default function RequireLocation({ children }: RequireLocationProps) {
    const location = useLocation()
    const navigate = useNavigate()
    const { requestData } = useRequest()
    const requestDataRef = useRef(requestData)
    requestDataRef.current = requestData

    const locationSelected = appStore(s => s.locationSelected)
    const allowCrawler = isSearchBot() && isCrawlablePublicPath(location.pathname)
    const [ready, setReady] = useState(locationSelected)
    const resolvingRef = useRef(false)

    useEffect(() => {
        if (allowCrawler) return

        if (locationSelected) {
            setReady(true)
            resolvingRef.current = false
            return
        }

        // Уже на экране выбора — ждём, пока пользователь выберет кинотеатр
        if (location.pathname === "/location" || location.pathname === "/auth") {
            return
        }

        if (resolvingRef.current) return
        resolvingRef.current = true
        setReady(false)

        let cancelled = false

        resolveLocationOrOpenPicker(requestDataRef.current, navigate, location).then(resolved => {
            if (!cancelled) setReady(resolved)
            resolvingRef.current = false
        })

        return () => {
            cancelled = true
        }
    }, [allowCrawler, locationSelected, location.pathname, location.search, navigate, location])

    // Робот видит афишу/фильм/контакты с кинотеатром по умолчанию, без пикера.
    // locationSelected не трогаем — главная для бота остаётся лендингом.
    if (allowCrawler) return <>{children}</>
    if (!locationSelected && !ready) return null
    if (!locationSelected) return null

    return <>{children}</>
}
